import {Query, QueryColumn, QueryNavigation} from "@/backend/queries"
import {BackendError, GraphQueryWarning, Models, useEnums, useProcesses} from '@hantera/portal-app'
import {computed, Ref, ref, watch} from "vue"
import {ColumnMap, ColumnMeta, QueryTableSelections, QueryTableSelectionsValue} from "./table-models";
import {BackendGraph} from "@/backend/graph";
import buildQuery from "./use-query-builder";
import {
  type DataColumn,
  type DataColumnFilter,
  type DataColumnFilterRange,
  type DataColumnFilterValue,
  type DataColumnOption,
  type DataColumnValues,
  type DataProvider,
  type DataRow
} from "@hantera/design-system";
import useGraphPhrases from "@/composition/use-graph-phrases";
import getColumnOptions from "./get-column-options";
import {AbsoluteDateRange, NumericRange, Scalar} from "./filter-models";
import Decimal from "decimal.js";
import log from "@/log";

const countCap = 10_000;

export default class QueryDataProvider implements DataProvider {
  
  private _rows = ref<DataRow[]>([])
  public rows = computed(() => this._rows.value)
  
  private _totalCount = ref(0)
  private _allLoaded = ref(false)
  public allLoaded = computed(() => this._allLoaded.value)
  private _columnMapping = ref<ColumnMap[]>([])
  public columnMapping = computed(() => this._columnMapping.value)
  private _isLoading = ref(false)
  public isLoading = computed(() => this._isLoading.value)
  
  public autoRefresh = ref(false)
  private autoRefreshInterval: ReturnType<typeof setInterval> | undefined;
  
  private _errors = ref<BackendError[]>()
  public errors = computed(() => this._errors.value)

  private _warnings = ref<GraphQueryWarning[]>([])
  public warnings = computed(() => this._warnings.value)
  
  public totalCount = computed(() => ({
    count: this._totalCount.value,
    cap: countCap
  }))

  public columns = computed(() => {
    return this.columnMapping.value.filter(r => r).map(r => {
      
      const queryFilters: QueryTableSelectionsValue|undefined = this.querySelections.filters[r.column.queryPath.join('.')]?.[r.column.fieldName]
      
      let values:DataColumnValues
      let canFilter = false
      let canSort = false
      let filters: DataColumnFilter[] = []

      if (!r.meta.field) {
        // This can happen if a query is configured to use a field that has been removed from the graph
        return {
          key: r.column.queryColumnId,
          valueIdx: r.sourceIdx,
          label: this.getColumnLabel(r),
          canFilter,
          canSort,
          size: r.column.size,
          filters,
        }
      }
      
      let valueType = r.meta.field.valueType
      let isArray = false
      if (valueType.type === 'array') {
        isArray = true
        valueType = valueType.itemType
      }
      
      // There are string fields that are filtered as enums, and we render those as enum fields as well, therefor use filter to determine type:
      if (valueType.type === 'enum') {
        
        if (r.meta.field.filter.type === 'enums') {
          canFilter = true
        }
        
        canSort = !isArray
        filters = mapScalarFilters()

        values = {
          type: 'enum',
          isArray,
          enums: this.enums.valueSets(r.meta.node, r.meta.field.name).value
        }
      } else if (valueType.type === 'scalar' && valueType.scalarType === 'instant') {
        
        if (r.meta.field.filter.type === 'instantRange') {
          canFilter = true
          canSort = true
          filters = mapRangeFilters()
          
          values = {
            type: 'instant',
            min: r.meta.field.filter.min
          }
        } else {
          values = {
            type: 'instant'
          }
        }
      } else if (valueType.type === 'scalar' && valueType.scalarType === 'decimal') {
        if (r.meta.field.filter.type === 'numericRange') {
          canFilter = true
          canSort = true
          filters = mapRangeFilters()

          values = {
            type: 'number',
            min: r.meta.field.filter.min,
            max: r.meta.field.filter.max
          }
        } else {
          values = {
            type: 'number'
          }
        }
      } else if (valueType.type === 'scalar' && valueType.scalarType === 'boolean') {
        if (r.meta.field.filter.type === 'boolean') {
          canFilter = true
          canSort = true
          filters = mapScalarFilters()

          values = {
            type: 'boolean'
          }
        } else {
          values = {
            type: 'boolean'
          }
        }
      } else {
        if (r.meta.field.filter.type !== 'none') {
          canFilter = true
          filters = mapScalarFilters()

          if (valueType.type !== 'scalar' || valueType.scalarType !== 'boolean') {
            canSort = true
          }
        }

        values = {
          type: 'text',
          isArray
        }
      }

      const fieldPath = this.getOrderByField(r)
      
      let sort: undefined | 'asc' | 'desc'
      if (this.querySelections.order && this.querySelections.order.field === fieldPath) {
        sort = this.querySelections.order.direction
      }
      
      return {
        key: r.column.queryColumnId,
        valueIdx: r.sourceIdx,
        label: this.getColumnLabel(r),
        values,
        canFilter,
        canSort,
        sort,
        size: r.column.size,
        filters,
      }

      function mapScalarFilters(): DataColumnFilterValue[] {
        if (!queryFilters) {
          return []
        }
        
        const result: DataColumnFilterValue[] = []
        for (const v of queryFilters.and) {
          const value = (v as Scalar).value
          result.push({
            type: 'all',
            value
          })
        }
        for (const v of queryFilters.or) {
          const value = (v as Scalar).value
          result.push({
            type: 'any',
            value
          })
        }
        for (const v of queryFilters.not) {
          const value = (v as Scalar).value
          result.push({
            type: 'not',
            value
          })
        }
        
        return result;
      }

      function mapRangeFilters(): DataColumnFilterRange[] {
        if (queryFilters?.or?.length === 1) {
          const filter = queryFilters.or[0]
          if (filter.type === 'dateRange' && filter.rangeType === 'absolute') {
            const range = filter as AbsoluteDateRange
            return [{
              from: new Date(range.from),
              to: new Date(range.to)
            }]
          } else if (filter.type === 'numericRange' && filter.type === 'numericRange') {
            const range = filter as NumericRange
            return [{
              from: new Decimal(range.from),
              to: new Decimal(range.to)
            }]
          }
        }

        return [];
      }
    })
  })
  
  private rootEdges: Record<string, Models.GraphMeta.GraphEdgeMeta> = {}
  private tableHeaders: string[] = []
  private lastCursor: string | undefined = undefined
  private previousGraphQuery: string | undefined
  private currentQuery: Query
  private currentSearchPhrase: string
  private currentSelections: QueryTableSelections
  private abortController: AbortController | undefined
  
  public constructor(
    private query: Query,
    private querySelections: QueryTableSelections,
    private graph: BackendGraph,
    private graphMeta: Record<string, Models.GraphMeta.GraphNodeMeta>,
    private processes: ReturnType<typeof useProcesses>,
    private graphPhrases: ReturnType<typeof useGraphPhrases>,
    private enums: ReturnType<typeof useEnums>,
    private searchPhrase: Ref<string>
  ) {
    this.currentQuery = query
    this.currentSelections = querySelections
    this.currentSearchPhrase = searchPhrase.value

    watch(query, () => {
      this.refreshInternal(false)
    }, {
      deep: true
    })
    
    watch(this.autoRefresh, () => {
      if (this.autoRefreshInterval) {
        clearInterval(this.autoRefreshInterval)
      }
      
      if (this.autoRefresh) {
        this.autoRefreshInterval = setInterval(() => this.refresh(), 5_000)
      }
    })
    
    let searchDebouncer: ReturnType<typeof setTimeout> | undefined
    watch(searchPhrase, () => {
      if (searchDebouncer) {
        clearTimeout(searchDebouncer)
      }

      searchDebouncer = setTimeout(() => {
        this.refreshInternal(true)
      }, 200)
    })

    this.refreshRootEdges()
    this.refreshColumnMapping()
  }

  private getColumnLabel(map: ColumnMap) {
    if (map.column.label) {
      return map.column.label;
    }

    let fieldName = map.column.fieldName;
    if (map.meta.field.dimensionKey) {
      // Strip dimension key from fieldName
      fieldName = fieldName.substring(0, fieldName.length - map.meta.field.dimensionKey.length - 1)
    }

    let result = this.graphPhrases.field(map.meta.node, fieldName);

    const dimensionKey = map.meta.field.dimensionKey;
    if (map.meta.field.dimension && dimensionKey) {
      result += ` (${dimensionKey})`
    }

    return result;
  }
  
  private refreshRootEdges() {
    this.rootEdges = Object.keys(this.graphMeta).reduce((res, nodeKey) => {
      const node = this.graphMeta[nodeKey];
      res[node.rootEdgeName] = {
        node: nodeKey,
        cardinality: 'many'
      };
      return res;
    }, {} as Record<string, Models.GraphMeta.GraphEdgeMeta>);
  }
  
  private refreshColumnMapping() {

    try {
      const invalidColumns: number[] = []
      this._columnMapping.value = this.currentQuery.columns.reduce((res, column, queryIdx) => {

        const meta = this.getFieldMeta(this.currentQuery.navigation, this.rootEdges, column);

        if (meta === undefined) {
          invalidColumns.push(queryIdx)
          return res;
        }

        const sourceIdx = this.tableHeaders.findIndex(f => f === (column.queryPath.join('.') + '.' + column.fieldName));

        res.push({
          sourceIdx: sourceIdx,
          column,
          meta
        });

        return res

      }, [] as ColumnMap[]);
      
      for (const columnIdx of invalidColumns) {
        const column = this.query.columns[columnIdx]
        if (column) {
          const fullPath = column.queryPath.slice(0)
          fullPath.push(column.fieldName)
          log.error('Dropped invalid column from query:', fullPath.join('.'))
        }
        this.query.columns.splice(columnIdx, 1)
      }
    }
    catch (error: any) {
      console.error('Unable to refresh meta', error)
    }
  }

  private getFieldMeta(
    nav: QueryNavigation,
    edges: Record<string, Models.GraphMeta.GraphEdgeMeta>,
    column: QueryColumn,
    parentPath: string = ''
  ): ColumnMeta | undefined {

    if (nav.edge === undefined) {
      return;
    }

    const currentPath = column.queryPath.join('.');
    const currentEdge = edges[nav.edge];
    if (currentEdge === undefined) {
      // This can happen if a query was made against an actor extension that has since been removed.
      log.error(`Attempted to query edge '${nav.edge}' which was not found`);
      return;
    }
    const currentNode = this.graphMeta[currentEdge.node];

    if (nav.edge && parentPath + nav.edge === currentPath) {

      if (column.fieldName.indexOf('->') !== -1) {

        const [fieldKey, _] = column.fieldName.split('->');
        const field = currentNode.fields[fieldKey]
        if (!field) {
          return
        }

        return {
          field,
          queryPath: column.queryPath,
          node: currentEdge.node
        }
      }
      
      const field = currentNode.fields[column.fieldName]
      if (!field) {
        return
      }

      return {
        field,
        queryPath: column.queryPath,
        node: currentEdge.node
      }
    }

    if (nav.navigation) {
      for (const subnav of nav.navigation) {
        const res = this.getFieldMeta(subnav, currentNode.edges, column, parentPath + nav.edge + '.');
        if (res) {
          return res;
        }
      }
    }

    return;
  }
  
  public async refresh() {
    await this.refreshInternal(true)
  }
  
  private async refreshInternal(force: boolean) {

    this.currentSearchPhrase = this.searchPhrase.value
    this.currentSelections = this.querySelections
    
    // Query may change at any time, so capture the query that we will use internally for now
    this.currentQuery = this.query
    
    this._errors.value = []
    
    // Now is also a good time to rebuild root edges from graph metadata
    this.refreshRootEdges();

    if (!this.currentQuery.navigation.edge) {
      // No query, clean table
      this._totalCount.value = 0
      this.tableHeaders = []
      this._rows.value = []
      this._allLoaded.value = true
      this.previousGraphQuery = undefined
      this.refreshColumnMapping()
      return;
    }

    const graphQuery = buildQuery(this.currentQuery, this.currentSelections, this.graphMeta, this.currentQuery.navigation, this.rootEdges, countCap);

    if (!graphQuery) {
      // Invalid query, clean table
      this._totalCount.value = 0
      this.tableHeaders = []
      this._rows.value = []
      this._allLoaded.value = true
      this.previousGraphQuery = undefined
      this.refreshColumnMapping()
      return;
    }
    
    if (this.currentSearchPhrase) {
      graphQuery.phrase = this.currentSearchPhrase
    }

    const graphQueryJson = JSON.stringify(graphQuery);
    if (!force && this.previousGraphQuery !== undefined && this.previousGraphQuery === graphQueryJson) {
      // Query has not changed, ignore
      this.refreshColumnMapping();
      return;
    }
    
    // Only indicate loading state if query has changed
    this._isLoading.value = this.previousGraphQuery === undefined || this.previousGraphQuery !== graphQueryJson
    
    this.previousGraphQuery = graphQueryJson;

    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();

    try {
      // The query view is user-composed, so we use warn validation to tolerate model changes
      const table = await this.graph.queryTable(graphQuery, undefined, 'warn');

      this._warnings.value = table.warnings ?? []
      this._totalCount.value = (table.totalCount ?? new Decimal(0)).toNumber();
      this.tableHeaders = table.headers;
      
      this._rows.value = table.rows.map(r => ({
        key: r.join(';'),
        values: r
      }))

      this.refreshColumnMapping();
      this.lastCursor = table.cursors.last
      if (table.totalCount !== undefined) {
        this._allLoaded.value = table.totalCount.lt(countCap) && table.totalCount.eq(table.rows.length)
      } else {
        this._allLoaded.value = table.rows.length < 100 // Page limit is 100 so fewer means probably all was loaded
      }
      
    } catch (e: unknown) {

      this._totalCount.value = 0
      this._rows.value = []
      this._errors.value = e as BackendError[]
      this._allLoaded.value = true
      this.tableHeaders = this.currentQuery.columns.map(r => r.queryPath.join('.') + '.' + r.fieldName)

      this.refreshColumnMapping();
    } finally {
      this.abortController = undefined;
      this._isLoading.value = false;
    }
  }
  
  public async loadMore() {
    
    if (this.allLoaded.value || !this.lastCursor || this.abortController) {
      return
    }
    
    const rootEdges = Object.keys(this.graphMeta).reduce((res, nodeKey) => {
      const node = this.graphMeta[nodeKey];
      res[node.rootEdgeName] = {
        node: nodeKey,
        cardinality: 'many'
      };
      return res;
    }, {} as Record<string, Models.GraphMeta.GraphEdgeMeta>)

    const query = buildQuery(this.currentQuery, this.currentSelections, this.graphMeta, this.currentQuery.navigation, rootEdges, false)

    if (!query) {
      // Invalid query, ignore
      return
    }
    
    if (this.currentSearchPhrase) {
      query.phrase = this.currentSearchPhrase
    }

    this._errors.value = []

    this.abortController = new AbortController()
    
    query.after = this.lastCursor

    try {
      const table = await this.graph.queryTable(query, this.abortController.signal)

      if (table.rows.length === 0) {
        this._allLoaded.value = true
      } else {
        this._rows.value.push(...table.rows.map(r => ({
          key: r.join(';'),
          values: r
        })))
        this.lastCursor = table.cursors.last
      }
    } catch (e: unknown) {
      this._errors.value = e as BackendError[]
    } finally {
      this.abortController = undefined
    }
  }
  
  public columnOptions = computed(() => {
    return getColumnOptions(this.graphMeta, this.graphPhrases, this._columnMapping.value, this.query)
  })
  
  public addColumn(option: DataColumnOption) {
    // _add is added in get-column-options.ts
    (option as any)._add?.()
  }
  public removeColumn(option: DataColumnOption) {
    const columnIdx = this.query.columns.findIndex(r => r.queryColumnId == option.key)
    if (columnIdx !== -1) {
      this.query.columns.splice(columnIdx, 1)
    }
  }
  
  public setColumnOrder(column: DataColumn, newIndex: number) {
    const currentColumnIdx = this.columns.value.findIndex(r => r.key === column.key)

    if (currentColumnIdx < 0) {
      return
    }
    
    const moving = this.query.columns.splice(currentColumnIdx, 1)
    if (newIndex > currentColumnIdx) {
      newIndex -= 1
    }
    
    this.query.columns.splice(newIndex, 0, ...moving)
  }
  
  public setColumnSize(columnKey: string, size?: number) {

    const column = this.query.columns.find(r => r.queryColumnId == columnKey)
    
    if (!column) {
      return
    }
    
    column.size = size
  }
  
  public setSort(column: DataColumn, direction?: 'asc' | 'desc') {
    
    if (!direction) {
      this.querySelections.order = undefined
      this.refreshInternal(true)
      return
    }
    
    let sortField: string | undefined
    const sortColumnMap = this._columnMapping.value.find(r => r.column.queryColumnId == column.key)
    if (sortColumnMap) {
      sortField = this.getOrderByField(sortColumnMap)
    }
    
    this.querySelections.order = sortField ? {
      field: sortField,
      direction: direction ?? 'asc'
    } : undefined
    
    this.refreshInternal(true)
  }
  
  public setFilters(column: DataColumn, filters?: DataColumnFilter[]) {
    
    const map = this._columnMapping.value?.find(r => r.column.queryColumnId == column.key)
    
    if (!map) {
      return
    }
    
    const queryPath = map.column.queryPath.join('.')
    
    if (!filters) {
      if (this.querySelections.filters[queryPath]?.[map.meta.field.name]) {
        delete this.querySelections.filters[queryPath][map.meta.field.name]
      }
    } else {

      if (!this.querySelections.filters[queryPath]) {
        this.querySelections.filters[queryPath] = {}
      }
      
      this.querySelections.filters[queryPath][map.meta.field.name] =
        filters.reduce((res: QueryTableSelectionsValue, f: DataColumnFilter): QueryTableSelectionsValue => {

          if ((f as DataColumnFilterValue).type) {
            const val = f as DataColumnFilterValue
            
            switch (val.type)
            {
              case 'all':
                res.and.push({
                  type: 'scalar',
                  value: val.value
                })
                break
              case 'any':
                res.or.push({
                  type: 'scalar',
                  value: val.value
                })
                break
              case 'not':
                res.not.push({
                  type: 'scalar',
                  value: val.value
                })
                break
            }
            
            return res
          } else {

            const range = f as DataColumnFilterRange
            if (column.values?.type === 'instant') {

              return {
                or: [{
                  type: 'dateRange',
                  rangeType: 'absolute',
                  from: (range.from as Date).toISOString(),
                  to: (range.to as Date).toISOString(),
                }],
                and: [],
                not: []
              }
            } else if (column.values?.type === 'number') {

              return {
                or: [{
                  type: 'numericRange',
                  from: (range.from as Decimal).toNumber(),
                  to: (range.to as Decimal).toNumber(),
                }],
                and: [],
                not: []
              }
            }
          }
          
          return res
        }, {
          and: [],
          or: [],
          not: []
        } as QueryTableSelectionsValue)
    }
    
    this.refreshInternal(true)
  }
  
  public setColumnLabel(column: DataColumn, label: string) {
    const queryColumn = this.query.columns.find(r => r.queryColumnId === column.key)
    
    if (!queryColumn) {
      return
    }
    
    if (label.trim()) {
      queryColumn.label = label.trim()
    } else {
      queryColumn.label = undefined
    }
  }

  private getOrderByField(column: ColumnMap) {
    if (column.meta.queryPath.length === 1) {
      return column.column.fieldName;
    }

    return column.meta.queryPath.map(f => '`' + f + '`').slice(1).join('.') + '.' + column.column.fieldName;
  }
}